import FileCopyIcon from '@mui/icons-material/FileCopy';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Box, Button, Divider, FormControl, Grid, IconButton, InputAdornment, InputLabel, Link, Select, Tab, Tabs, TextField, useTheme } from '@mui/material';
import grey from '@mui/material/colors/grey';
import { makeStyles } from '@mui/styles';
import jsonpath from 'jsonpath';
import MaterialUiPhoneNumber from 'material-ui-phone-number';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { Prompt } from 'react-router';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { DataAPIContext, DELETE, GET_ONE, UPDATE } from '../api';
import AccessDeniedView from '../components/AccessDeniedView';
import AccessTagsDropdown from '../components/AccessTagsDropdown';
import CacheRefreshHistory from '../components/CacheRefreshHistory';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import ImageGalleryEditor from '../components/ImageGalleryEditor';
import LicenseeFormField from '../components/LicenseeFormField';
import NavigateBackBar from '../components/NavigateBackBar';
import PageMeta from '../components/PageMeta';
import PriceDisplay from '../components/PriceDisplay';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import QuillEditor from '../components/QuillEditor';
import { RecordDetails, RecordDetailsContext } from '../components/RecordDetails';
import RevisionHistoryDialog from '../components/RevisionHistoryDialog';
import { a11yProps, TabPanel } from '../components/TabPanel';
import UsedByPanel from '../components/UsedByPanel';
import { ContextStore } from '../store/contextStore';
import { checkAndAddHttpPrefix, copyTextToClipboard, fromServerErrors, GMapContext, readableDate } from '../utils';
import { useIsWidthUp } from '../utils';
import validURL from '../utils/validators/url';
import { validateCoordinateFields } from '../utils/validators/coordinates';
import HoursOfOperation from './ActivityHoursOfOperation';
import ActivityOntripEditor from './ActivityOntrip';
import ActivityReviews from './ActivityReviews';
import PlaceButton from './PlaceButton';
import PlacePhotosDialog from './PlacePhotosDialog';
import PlaceSearch from './PlaceSearch';
import PlaceWrapper from './PlaceWrapper';
import { activityTypeMenuItems, ConfirmationDialogText, populateFromPlace, PRICE_DISPLAY_PRICE_RANGE, setActivityInitialState } from './utils';

const useStyles = makeStyles((theme) => ({
    root: {
        borderRadius: '12px',
        margin: theme.spacing(2),
        marginTop: '2rem',
        border: 'solid 1px #D8D8D8',
        overflow: 'auto',
        height: `calc(${theme.cp.contentHeight} - 1rem)`,
        position: 'relative',
        [theme.breakpoints.down('sm')]: {
            margin: 0,
            height: `calc(${theme.cp.contentHeight} - 1rem)`,
        },
    },
    buttonPanel: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: grey[100],
    },
}));

const validateInput = (activity, hasChanged) => {
    const errors = {};
    let canSave = true;
    if (!activity.name || activity.name.length <= 0) {
        if (hasChanged) {
            errors.name = { error: true, helperText: 'Activity name is required' };
        }
        canSave = false;
    }
    const { valid: coordsValid, errors: coordErrors } = validateCoordinateFields(
        activity.latitude,
        activity.longitude,
    );
    if (!coordsValid) {
        Object.assign(errors, coordErrors);
        canSave = false;
    }
    return { canSave, errors };
};

const getComparableState = (activity) => {
    const a = { ...activity };
    // remove everything that is synced separately
    delete a.documents;
    delete a.images;
    delete a.ratings;
    return JSON.stringify(a);
};

const ActivityEditForm = (props) => {
    const gmapNs = React.useContext(GMapContext);
    const dataAPI = React.useContext(DataAPIContext);
    const urlParams = useParams();
    const location = useLocation();
    const classes = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const { settings, hasPerm } = React.useContext(ContextStore);
    const googleApiKey = settings.google_maps_key;
    const resource = 'activity';
    const paramsToTab = {
        general: 0,
        pricing: 1,
        reviews: 2,
        images: 3,
        ontrip: 4,
        changes: 5,
        used_by: 6,
    };
    const currentTab = paramsToTab[urlParams.tab] ? paramsToTab[urlParams.tab] : 0;
    const [activity, setActivity] = React.useState(setActivityInitialState(props.data));
    const [mapClickMode, setMapClickMode] = React.useState(false);
    const [syncActive, setSyncActive] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const isSmallScreen = !useIsWidthUp('sm');
    const [originalState, setOriginalState] = React.useState(getComparableState(activity));
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [isPlacePhotosDialogOpen, setIsPlacePhotosDialogOpen] = React.useState(false);
    const [showCacheHistory, setShowCacheHistory] = React.useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [stillDownloadingImages, setStillDownloadingImages] = React.useState(false);
    const [clientErrors, setClientErrors] = React.useState({ amountsNotEntered: false, websiteError: false });
    const currentState = getComparableState(activity);
    const hasChanged = originalState !== currentState;
    const activityTitleLength = isSmallScreen ? 40 : 120;
    const hasWritePerm = hasPerm('activity', 'write')

    const onLicenseeChange = (id) => {
        setActivity((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };

    React.useEffect(() => {
        if (paramsToTab[urlParams.tab] === void 0) {
            history.replace(`${location.pathname}/general`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTab, urlParams.id, urlParams.tab]);

    const tabPanelProps = (index) => {
        return {
            // className: classes.tabPanel,
            value: currentTab,
            index: index,
            dir: theme.direction,
            // buttonBoxSpacing: true,
            style: {
                padding: theme.spacing(2),
                //Need to match theme.cp.contentHeight - button box - tabs menu
                height: isSmallScreen ? 'calc(100svh - 18rem)' : 'calc(100svh - 21rem)',
                overflow: 'auto',
            },
        };
    };

    const handleTabChange = (ev, newTab) => {
        const match = Object.keys(paramsToTab).filter((x) => paramsToTab[x] === newTab);
        if (match && match.length === 1) {
            history.push(`/activity/${urlParams.id}/${match[0]}`);
        }
    };


    const fetchPlaceDescription = (place_id) => {
        dataAPI(GET_ONE, resource, {
            urlSuffix: 'get_place_details',
            filter: {
                place_id: place_id,
            },
        }).then((res) => {
            const details = res.data.result;
            if (details.editorial_summary && details.editorial_summary.overview) {
                setActivity(prev => ({ ...prev, description: details.editorial_summary.overview }));
            }
        });
    };

    const onPlacePhotosSave = (replace, newPhotos) => {
        let selected = [];
        if (newPhotos) {
            selected = [...newPhotos];
        } else {
            selected = (activity.place_photos || []).map((x, i) => ({ ...x, idx: i })).filter((x) => !!x.selected);
        }

        onPlacePhotosDialogClose();
        if (selected.length > 0) {
            setSyncActive(true);
            dataAPI(UPDATE, resource, {
                id: activity.id,
                urlSuffix: 'schedule_image_download/',
                data: {
                    replace: replace,
                    images: selected,
                },
            })
                .then((res) => {
                    if (res.statusCode === 200) {
                        if (selected.length === 1) {
                            enqueueSnackbar(`${selected.length} image is being downloaded`);
                        } else {
                            enqueueSnackbar(`${selected.length} images are being downloaded`);
                        }
                        setActivity(prev => ({ ...prev, images: res.data }));
                    } else {
                        enqueueSnackbar(`Unexpected HTTP code: ${res.statusCode}`, { variant: 'error', autoHideDuration: 5000 });
                    }
                })
                .catch((err, ...args) => {
                    enqueueSnackbar('Error scheduling images for download', { variant: 'error' });
                })
                .finally(() => {
                    setSyncActive(false);
                });
        } else {
            enqueueSnackbar('No images were selected for upload', { variant: 'default' });
        }
    };

    const toGalleryFormat = (list) => {
        return (list || []).map((x) => ({
            // `src` is resolved by placeToLegacy: getURI() is a method and a place
            // travels to the backend through JSON.stringify. Nothing calls the
            // deprecated PlacePhoto.getUrl() any more.
            src: x.src,
            selected: !!x.selected,
            width: 1,
            height: 1,
        }));
    };


    const handlePlaceSelected = async (p) => {
        if (!p) {
            setActivity(setPlace({ place: null, populate: false }));
            return;
        }
        await onPlacePhotosSave(true, toGalleryFormat(p?.photos));
        let new_activity = setPlace({ place: p, populate: !!p });
        setActivity(new_activity);

        if (p && p.place_id) {
            fetchPlaceDescription(p.place_id);
        }
    };

    const setPlace = ({ place, populate = false } = {}) => {
        let p = place;
        const pw = new PlaceWrapper(p);
        let new_activity = {
            ...activity,
            place_data: p || null,
            place_id: p?.place_id || null,
            place_photos: pw.photos(/* selectAll = */ true),
        };
        if (populate) {
            new_activity = populateFromPlace(p, new_activity);
        }
        return new_activity;
    }

    const removeErrorFor = (id) => () => {
        if (errors && errors[id]) {
            const nerr = { ...errors };
            delete nerr[id];
            setErrors(nerr);
        }
    };

    const errorAttrs = (id) => (errors && errors[id] ? { error: true, helperText: errors[id], clearError: removeErrorFor(id) } : {});

    const genHandleTextChange = (field, cvfn) => (ev) => {
        let val = cvfn ? cvfn(ev.target.value, field) : ev.target.value;
        removeErrorFor(field)();
        if((field === 'latitude' || field === 'longitude') && val === '') {
            val = null;
        }
        setActivity(prev => ({ ...prev, [field]: val }));
    };

    const handleDescriptionChange = (content) => {
        setActivity(prev => ({ ...prev, description: content }));

    }

    const onNameChange = (name) => {
        setActivity(prev => ({ ...prev, name: name }));

    };
    const onCustomIdChange = (custom_id) => {
        setActivity(prev => ({ ...prev, custom_id: custom_id.target.value }));

    };

    const handleOnPhoneChange = (number, numberProps) => {
        setActivity(prev => ({ ...prev, phone: number }));
    };

    const genHandleSelect = (field, cvfn) => (ev) => {
        const val = cvfn ? cvfn(ev.target.value, field) : ev.target.value;
        removeErrorFor(field);

        setActivity(prev => ({ ...prev, [field]: val }));
    };

    const onPriceDisplayChange = (act) => {
        setActivity(prev => ({
            ...prev, price_display: act.price_display,
            payment_type: act.payment_type,
            price_type: act.price_type,
            cost_min: act.cost_min,
            cost_max: act.cost_max,
            price_level: act.price_level,
            cost_prefix: act.cost_prefix,
            cost_unit: act.cost_unit,
            currency: act.currency
        }));

    };

    const handleScheduleChange = (schedule) => {
        setActivity(prev => ({ ...prev, opening_hours: schedule }));
    };

    const onImagesChange = (gallery) => {
        setActivity(prev => ({ ...prev, images: gallery }))
    };

    const onDocumentsChange = (docs) => {
        setActivity(prev => ({ ...prev, documents: docs }))

    };

    const onAccessTagsChange = (ev, atags, reason) => {
        setActivity(prev => {
            return ({ ...prev, atag: atags })
        });
    };

    const onCategoryChange = (ev, nv) => {
        setActivity(prev => ({ ...prev, category: nv }));
    };

    const onDocumentPathChange = (update) => {
        setActivity((prevState) => {
            let new_activity = { ...prevState };
            for (const k in update) {
                if (!update.hasOwnProperty(k)) {
                    continue;
                }
                const setValueByPath = (path, value) => {
                    jsonpath.value(new_activity, path, value);
                }
                setValueByPath(`$.${k}`, update[k]);
            }
            return { ...prevState, ...new_activity };
        });
    };

    const onNearbyChange = (nearby) => {
        setActivity((prevState) => {
            return { ...prevState, children: nearby };
        });
    };

    const onRatingsChange = (ratings) => {
        setActivity(prev => ({ ...prev, ratings: ratings }));
    };

    const handleUseMapClick = (ev) => {
        setMapClickMode(!mapClickMode);
    };

    const handleCancelUseMapClick = (ev) => {
        setMapClickMode(!mapClickMode);
    };

    const handleLocationSelected = (latLng) => {
        if (latLng !== null) {
            setActivity(prev => ({ ...prev, latitude: latLng.lat().toFixed(7), longitude: latLng.lng().toFixed(7) }));
        }
        setMapClickMode(false);
    };

    const doSave = (ev) => {
        setSyncActive(true);
        dataAPI(UPDATE, resource, { id: activity.id, data: activity })
            .then((res) => {
                if (res.statusCode >= 400) {
                    if (res.statusCode === 406 && res.data?.errors) {
                        const fieldErrors = fromServerErrors(res.data);
                        const stringErrors = {};
                        Object.entries(fieldErrors).forEach(([field, msgs]) => {
                            if (Array.isArray(msgs) && msgs.length) {
                                stringErrors[field] = msgs.join(', ');
                            } else if (typeof msgs === 'string') {
                                stringErrors[field] = msgs;
                            }
                        });
                        setErrors(stringErrors);
                        const inlineMessages = Object.values(stringErrors).filter(Boolean);
                        enqueueSnackbar(
                            inlineMessages.length ? inlineMessages.join(' ') : (res.data?.message || 'Please check for errors'),
                            { variant: 'error', autoHideDuration: 5000 },
                        );
                    } else {
                        enqueueSnackbar(res.data?.message || 'Error saving activity', { variant: 'error' });
                    }
                    return;
                }
                enqueueSnackbar('Activity saved', { variant: 'success' });
                setActivity(setActivityInitialState(res.data));
                setOriginalState(getComparableState(setActivityInitialState(res.data)));
            })
            .catch((err, ...args) => {
                console.error('Activity save error', err, args);
                enqueueSnackbar('Error saving activity', { variant: 'error' });
            })
            .finally(() => {
                setSyncActive(false);
            });
    };

    const handleSave = (ev) => {
        checkAndAddHttpPrefix(activity.website);
        if (clientErrors.amountsNotEntered) {
            if (activity.price_display === PRICE_DISPLAY_PRICE_RANGE) {
                if (activity.cost_min > activity.cost_max) {
                    enqueueSnackbar("Price amount is present in the Price Max field but not valid", { variant: 'error', autoHideDuration: 5000 });
                    return;
                }
            }
            enqueueSnackbar("Please enter price amount to save", { variant: 'error', autoHideDuration: 5000 });
            return;
        }
        doSave();
    };

    const doDelete = (ev) => {
        setSyncActive(true);
        const id = activity.id;
        if (id) {
            dataAPI(DELETE, resource, { id: id })
                .then((res) => {
                    if (res.statusCode === 406) {
                        enqueueSnackbar(res.data.message, { variant: 'error', autoHideDuration: 5000 });
                    } else if (res.statusCode === 200 || res.statusCode === 204) {
                        enqueueSnackbar('Activity deleted', { variant: 'success' });
                        history.push('/activity');
                    } else {
                        enqueueSnackbar('Unexpected HTTP error: ' + res.statusCode, { variant: 'error' });
                    }
                })
                .catch((err, ...args) => {
                    enqueueSnackbar('Error deleting activity', { variant: 'error' });
                })
                .finally(() => {
                    setSyncActive(false);
                });
        } else {
            enqueueSnackbar('Activity deleted', { variant: 'default' });
            history.push('/activity');
        }
    };


    const setPhotos = (photos, selectAll) => {
        const pw = new PlaceWrapper({ photos });
        // A copy, not `activity` itself. Assigning onto the current state object and
        // handing it back to setActivity means the "next" state is reference-equal to
        // the previous one, so React can bail out of the re-render and the gallery
        // shows nothing until some other state change happens to flush it.
        return { ...activity, place_photos: pw.photos(/* selectAll = */ selectAll) };
    }
    const onPlacePhotosChange = (newlist) => {
        setActivity(setPhotos(newlist));
    };

    const onPlacePhotosDialogClose = () => {
        setIsPlacePhotosDialogOpen(false);
    };

    const onPlacePhotosLoad = (place) => {
        setActivity(setPhotos(place.photos, /* selectAll = */ true));
        setIsPlacePhotosDialogOpen(true);
    };

    const toggleCacheRefreshHistory = () => {
        setShowCacheHistory((prevState) => !prevState);
    };

    const copyToClipboard = (text, label) => {
        if (text) {
            copyTextToClipboard(text);
            enqueueSnackbar(label ? `${label} copied!` : 'Copied!', { variant: 'success', autoHideDuration: 500 });
        } else {
            enqueueSnackbar(label ? `${label} is empty!` : 'Cannot copy empty string!', { variant: 'error', autoHideDuration: 500 });
        }
    };

    const lat = activity.latitude;
    const lng = activity.longitude;


    const { canSave, errors: validationErrors } = validateInput(activity, hasChanged);

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        switch (confirmationDialogAction) {
            case 'save':
                doSave();
                break;
            case 'delete':
                doDelete();
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };


    const handleWebsiteURLOnBlur = () => {
        if (validURL(activity.website)) {
            setClientErrors(prev => ({ ...prev, websiteError: false }))
        } else {
            setClientErrors(prev => ({ ...prev, websiteError: true }))

        }
    };

    if (!hasPerm('activity', 'read')) {
        return <AccessDeniedView />;
    }

    const activityListUrl = props.lastURLParams?.size > 0 ? '/activity?' + props.lastURLParams?.toString() : '/activity';

    return (
        <FormLicenseeContext.Provider value={toLicenseeId(activity.tour_operator)}>
        <>
            <div className={classes.root}>
                <PageMeta title={`Edit Activity - ${props.data.display_name}`} />
                <Prompt
                    when={hasChanged && hasPerm('activity', 'write')}
                    message={(loc, act) => {
                        return loc.pathname.startsWith(`/activity/${urlParams.id}`) ? true : 'Activity: are you sure you want to leave without saving changes?';
                    }}
                />
                <CacheRefreshHistory resource="activity" id={urlParams.id} open={showCacheHistory} onClose={toggleCacheRefreshHistory} />
                {confirmationDialogAction ? (
                    <ConfirmationDialog
                        classes={{ paper: classes.paper }}
                        keepMounted
                        onCancel={confirmationDialogCancel}
                        onConfirm={confirmationDialogConfirm}
                        title={ConfirmationDialogText[confirmationDialogAction].title}
                        okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                        cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                        open={!!confirmationDialogAction}
                    >
                        {ConfirmationDialogText[confirmationDialogAction].body}
                    </ConfirmationDialog>
                ) : null}
                <PlacePhotosDialog images={activity.place_photos} open={isPlacePhotosDialogOpen} onChange={onPlacePhotosChange} onSave={onPlacePhotosSave} onClose={onPlacePhotosDialogClose} existingImagesCount={(activity.images || []).length} isSmallScreen={isSmallScreen} />
                <FixedLinearProgress visibility={syncActive} />
                <NavigateBackBar label={activity.name} url={activityListUrl} maxLabelLength={activityTitleLength} />
                <Divider />
                <Grid container direction="column">
                    <Grid item xs={12}>
                        <Tabs value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant={isSmallScreen ? 'scrollable' : 'scrollable'} style={{ maxWidth: '99vw' }}>
                            <Tab label="General Info" {...a11yProps(0)} />
                            <Tab label="Contacts &amp; Pricing" {...a11yProps(1)} />
                            <Tab label="Reviews" {...a11yProps(2)} />
                            <Tab label="Images" {...a11yProps(3)} />
                            <Tab label="Documents &amp; Links" {...a11yProps(4)} />
                            {hasPerm('activity', 'view_history') && <Tab label="Change history" {...a11yProps(5)} />}
                            <Tab label="Used by" {...a11yProps(6)} />
                        </Tabs>
                        <Divider />
                        <TabPanel {...tabPanelProps(0)}>
                            <Box mb={2}>
                                <LicenseeFormField value={activity.tour_operator} onChange={onLicenseeChange} disabled={!hasWritePerm} />
                            </Box>
                            <PlaceSearch
                                {...errorAttrs('name')}
                                {...validationErrors.name}
                                googleApiKey={googleApiKey}
                                disabled={!hasWritePerm}
                                ns={gmapNs.id()}
                                theme={theme}
                                lat={lat}
                                lng={lng}
                                mapClickMode={mapClickMode}
                                onLocationSelected={handleLocationSelected}
                                onPlaceSelected={handlePlaceSelected}
                                onNameChange={onNameChange}
                                isSmallScreen={isSmallScreen}
                                handleCancelUseMapClick={handleCancelUseMapClick}
                                adornment={
                                    <InputAdornment position="start">
                                        <LocationOnIcon />
                                    </InputAdornment>
                                }
                                name="Name"
                                value={activity.name}
                            />
                            <h4>Description</h4>
                            <Grid container direction="row" spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField {...errorAttrs('display_name')} disabled={!hasWritePerm} value={activity.display_name} variant="filled" size="small" fullWidth label="Display Name" onChange={genHandleTextChange('display_name')} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl variant="filled" size="small" disabled={!hasWritePerm} fullWidth>
                                        <InputLabel>Activity Type</InputLabel>
                                        <Select {...errorAttrs('type')} value={activity.type} onChange={genHandleSelect('type', (v) => +v)}>
                                            {activityTypeMenuItems}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <RemoteAutocomplete
                                        resource="activity_category"
                                        urlSuffix={null}
                                        label="Category"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        disabled={!hasWritePerm}
                                        value={activity.category}
                                        onChange={onCategoryChange}
                                        getOptionLabel={(opt) => opt.title || ''}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField {...errorAttrs('preview_text')} disabled={!hasWritePerm} value={activity.preview_text} onChange={genHandleTextChange('preview_text')} variant="filled" size="small" label="Preview Text (optional)" multiline fullWidth />
                                </Grid>
                                <Grid item xs={12}>
                                    <InputLabel style={{ marginBottom: '-0.5rem' }} shrink>Description (optional)</InputLabel>
                                    <QuillEditor
                                        value={activity?.description}
                                        disabled={!hasWritePerm}
                                        onChange={(content) => handleDescriptionChange(content)}
                                    />
                                </Grid>
                            </Grid>
                            <h4>Duration</h4>
                            <Grid container direction="row" spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        helperText="Hours as decimal (1.5 means an hour and a half)"
                                        {...errorAttrs('min_duration')}
                                        value={activity.min_duration}
                                        onChange={genHandleTextChange('min_duration')}
                                        type="number"
                                        variant="filled"
                                        size="small"
                                        label="Min Duration (optional)"
                                        fullWidth
                                        disabled={!hasWritePerm}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        helperText="Hours as decimal (1.5 means an hour and a half)"
                                        {...errorAttrs('max_duration')}
                                        value={activity.max_duration}
                                        onChange={genHandleTextChange('max_duration')}
                                        type="number"
                                        variant="filled"
                                        size="small"
                                        disabled={!hasWritePerm}
                                        label="Max Duration (optional)"
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container direction="row" spacing={2}>
                                <Grid item xs={6} sm={1}>
                                    <h4>Coordinates</h4>
                                </Grid>
                                <Grid item xs={6} sm={2}>
                                    <Box mt={2} ml={4} mr={1}>
                                        <Button color="primary" size="small" onClick={handleUseMapClick} disabled={!hasWritePerm}>
                                            Use map
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Grid container direction="row" spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField {...errorAttrs('latitude')} {...validationErrors.latitude} disabled={!hasWritePerm} value={activity.latitude} onChange={genHandleTextChange('latitude')} type="number" variant="filled" size="small" label="Latitude (optional)" fullWidth InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField {...errorAttrs('longitude')} {...validationErrors.longitude} disabled={!hasWritePerm} value={activity.longitude} onChange={genHandleTextChange('longitude')} type="number" variant="filled" size="small" label="Longitude (optional)" fullWidth InputLabelProps={{ shrink: true }} />
                                </Grid>
                            </Grid>

                            <h4>Summary</h4>
                            <Grid container direction="row" spacing={2} alignItems="flex-start">
                                <Grid item xs={12} sm={4}>
                                    <strong>{activity.name}</strong>
                                    {readableDate(activity.created_ts, 'Created')}
                                    {readableDate(activity.updated_ts, 'Updated')}
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    {activity.latitude && activity.longitude ? (
                                        <Box display="inline">
                                            Maps:&nbsp;
                                            <Link title="Open on Google Maps" href={`https://www.google.com/maps/place/${activity.latitude},${activity.longitude}/@${activity.latitude},${activity.longitude},15z`} target="_blank">
                                                Google
                                            </Link>
                                            ,&nbsp;
                                            <Link title="Open on OpenStreetMap" href={`https://www.openstreetmap.org/?mlat=${activity.latitude}&mlon=${activity.longitude}&zoom=12&layers=M`} target="_blank">
                                                OSM
                                            </Link>
                                            ,&nbsp;
                                            <Link title="Open on Bing Maps" href={`https://www.bing.com/maps/?v=2&cp=${activity.latitude}~${activity.longitude}&style=r&lvl=15&sp=Point.${activity.latitude}_${activity.longitude}_`} target="_blank">
                                                Bing
                                            </Link>
                                        </Box>
                                    ) : null}
                                </Grid>
                                <Grid item xs={isSmallScreen ? 12 : 4} container spacing={2}>
                                    <Grid item xs={12}>
                                        <AccessTagsDropdown disabled={!hasWritePerm} value={activity.atag || []} onChange={onAccessTagsChange} />
                                    </Grid>
                                    <Grid item xs={12} sm={12}>
                                        <TextField
                                            variant="filled"
                                            onClick={toggleCacheRefreshHistory}
                                            disabled={!hasWritePerm}
                                            color="primary"
                                            title="External ID"
                                            label={`ID`}
                                            value={activity.id}
                                            fullWidth
                                            InputProps={{
                                                readOnly: true,
                                                endAdornment: (
                                                    <IconButton
                                                        aria-label="copy activity id to clipboard"
                                                        onClick={() => {
                                                            copyToClipboard(activity.id, 'Activity ID');
                                                            toggleCacheRefreshHistory();
                                                        }}
                                                        edge="end"
                                                    >
                                                        <FileCopyIcon />
                                                    </IconButton>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12}>
                                        <TextField
                                            variant="filled"
                                            color="primary"
                                            title="External ID"
                                            label={`External ID`}
                                            disabled={!hasWritePerm}
                                            value={activity.custom_id}
                                            placeholder={'Set external ID'}
                                            onChange={onCustomIdChange}
                                            fullWidth
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        aria-label="copy custom activity id to clipboard"
                                                        onClick={() => {
                                                            copyToClipboard(activity.custom_id, 'Activity custom ID');
                                                        }}
                                                        edge="end"
                                                    >
                                                        <FileCopyIcon />
                                                    </IconButton>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                                {activity.place_id ? (
                                    <Box display="flex" alignItems="center" pl={2}>
                                        Place ID:&nbsp;
                                        <Link href={`https://www.google.com/maps/place/?q=place_id:${activity.place_id}`} target="_blank">
                                            {activity.place_id}
                                        </Link>
                                        <IconButton size="small" onClick={(ev) => copyToClipboard(activity.place_id, 'Place ID')}>
                                            <FileCopyOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ) : null}
                            </Grid>
                        </TabPanel>
                        <TabPanel {...tabPanelProps(1)}>
                            <h4>Contacts</h4>
                            <Grid container direction="row">
                                <Grid item xs={12}>
                                    <Box my={1}>
                                        <TextField
                                            {...errorAttrs('website')}
                                            value={activity.website}
                                            onChange={genHandleTextChange('website')}
                                            type="url"
                                            disabled={!hasWritePerm}
                                            variant="filled"
                                            size="small"
                                            label="Website"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            onBlur={handleWebsiteURLOnBlur}
                                            error={clientErrors.websiteError}
                                            helperText={clientErrors.websiteError && 'Please enter a valid URL.'}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box my={1}>
                                        <MaterialUiPhoneNumber
                                            defaultCountry={'us'}
                                            disabled={!hasWritePerm}
                                            helperText="Country code MUST be included. Do not include any special characters except '+'"
                                            {...errorAttrs('phone')}
                                            variant="filled"
                                            name="phone"
                                            label="Phone"
                                            fullWidth
                                            inputProps={{ name: 'phone' }}
                                            onChange={handleOnPhoneChange}
                                            value={activity.phone || ''}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box my={1}>
                                        <TextField {...errorAttrs('address')} disabled={!hasWritePerm} value={activity.address} onChange={genHandleTextChange('address')} type="address" variant="filled" size="small" label="Address" fullWidth InputLabelProps={{ shrink: true }} />
                                    </Box>
                                </Grid>
                            </Grid>
                            <h4>Internal Notes</h4>
                            <Grid item xs={12}>
                                <Box my={1}>
                                    <TextField
                                        {...errorAttrs('internal_notes')}
                                        value={activity.internal_notes}
                                        disabled={!hasWritePerm}
                                        onChange={genHandleTextChange('internal_notes')}
                                        type="text"
                                        variant="filled"
                                        size="small"
                                        label="Type internal notes here"
                                        fullWidth
                                        multiline
                                        InputLabelProps={{ shrink: true }}
                                        helperText="Won't be shown in client apps"
                                    />
                                </Box>
                            </Grid>
                            <h4>Hours of Operation</h4>
                            <HoursOfOperation disabled={!hasWritePerm} schedule={activity.opening_hours} isSmallScreen={isSmallScreen} onChange={handleScheduleChange} />
                            <h4>Pricing</h4>
                            <PriceDisplay object={activity} disabled={!hasWritePerm} setClientErrors={setClientErrors} clientErrors={clientErrors} onChange={onPriceDisplayChange} />
                        </TabPanel>
                        <TabPanel {...tabPanelProps(2)}>
                            <ActivityReviews data={activity.ratings} disabled={!hasWritePerm} resource="activity_ratings" resourceMeta={{ activity: activity.id }} onChange={onRatingsChange} isSmallScreen={isSmallScreen} />
                        </TabPanel>
                        <TabPanel {...tabPanelProps(3)}>
                            {!isNaN(urlParams.subtab) && isSmallScreen ? null : <PlaceButton googleApiKey={googleApiKey} placeId={activity.place_id} onLoad={onPlacePhotosLoad} />}
                            <ImageGalleryEditor
                                key={JSON.stringify(activity.images)}
                                list={activity.images}
                                resource="activity_images"
                                resourceMeta={{ activity: activity.id }}
                                onChange={onImagesChange}
                                isSmallScreen={isSmallScreen}
                                navigationPrefix={`/activity/${activity.id}/images`}
                                imageId={!isNaN(urlParams.subtab) ? urlParams.subtab : null}
                                setStillDownloadingImages={setStillDownloadingImages}
                                hasEditPermission={hasPerm('activity', 'write')}
                                hasDeletePermission={hasPerm('activity', 'delete')}
                            />
                        </TabPanel>
                        <TabPanel {...tabPanelProps(4)}>
                            <ActivityOntripEditor
                                disabled={!hasWritePerm}
                                data={activity.documents}
                                nearby={activity.children}
                                resource="activity_documents"
                                resourceMeta={{ activity: activity.id }}
                                onChange={onDocumentsChange}
                                onPathChange={onDocumentPathChange}
                                onNearbyPathChange={onDocumentPathChange}
                                onNearbyChange={onNearbyChange}
                                isSmallScreen={isSmallScreen}
                            />
                        </TabPanel>
                        <TabPanel value={currentTab} index={5} className={isSmallScreen ? classes.horizontalTab : classes.verticalTab} idprefix={!isSmallScreen ? 'vertical-tab' : null}>
                            <RevisionHistoryDialog open={currentTab === 5} resource={resource} id={activity.id} />
                        </TabPanel>
                        <TabPanel {...tabPanelProps(6)}>
                            <UsedByPanel resource={resource} id={activity.id} />
                        </TabPanel>
                    </Grid>
                    <Box p={1} className={classes.buttonPanel}>
                        <FormActionButtons
                            mode="edit"
                            onSubmit={handleSave}
                            submitDisabled={!hasPerm('activity', 'write') || syncActive || !hasChanged || !canSave || clientErrors.websiteError || stillDownloadingImages}
                            onCancel={() => history.push(activityListUrl)}
                            cancelDisabled={syncActive}
                        />
                    </Box>
                </Grid>
            </div>
            <br />
        </>
        </FormLicenseeContext.Provider>
    );
};

const ActivityEdit = (props) => {
    const resource = 'activity';
    const routeParams = useParams();
    const activityId = routeParams.id;
    const { hasPerm } = React.useContext(ContextStore);

    if (!hasPerm(resource, 'read')) {
        return <AccessDeniedView />
    }

    return (
        <RecordDetails resource={resource} id={activityId}>
            <RecordDetailsContext.Consumer>{(data) => <ActivityEditForm lastURLParams={props.lastURLParams} resource={props.resource} id={props.id} data={data} />}</RecordDetailsContext.Consumer>
        </RecordDetails>
    );
};

export default ActivityEdit;
